import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { Eye, EyeOff, ShieldCheck, Loader2, Check, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const plans = [
  {
    id: 'starter',
    name: 'Starter',
    price: '29',
    description: 'Für kleine Betriebe',
    features: ['1 Kassenplatz', 'TSE inklusive', 'Basis-Berichte']
  },
  {
    id: 'professional',
    name: 'Professional',
    price: '59',
    description: 'Für wachsende Gastronomien',
    features: ['Bis zu 3 Kassenplätze', 'DATEV-Export', 'Tischverwaltung'],
    popular: true
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: '99',
    description: 'Für Gruppen & Ketten',
    features: ['Unbegrenzte Kassen', 'Multi-Standort', 'API-Zugang']
  }
];

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedPlan = searchParams.get('plan') || 'professional';
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    password_confirm: '',
    company_name: '',
    contact_name: '',
    plan: preselectedPlan
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'E-Mail ist erforderlich';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Ungültige E-Mail-Adresse';
    }
    
    if (!formData.password) {
      newErrors.password = 'Passwort ist erforderlich';
    } else if (formData.password.length < 10) {
      newErrors.password = 'Mindestens 10 Zeichen';
    } else if (!/\d/.test(formData.password)) {
      newErrors.password = 'Muss eine Zahl enthalten';
    } else if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(formData.password)) {
      newErrors.password = 'Muss ein Sonderzeichen enthalten';
    }
    
    if (formData.password !== formData.password_confirm) {
      newErrors.password_confirm = 'Passwörter stimmen nicht überein';
    }
    
    if (!formData.company_name) {
      newErrors.company_name = 'Firmenname ist erforderlich';
    }
    
    if (!formData.contact_name) {
      newErrors.contact_name = 'Ansprechpartner ist erforderlich';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Registrierung fehlgeschlagen');
      }
      
      // Store token (both keys for compatibility)
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('cashx_user', JSON.stringify(data.user));
      localStorage.setItem('cashx_tenant', JSON.stringify(data.tenant));
      
      toast.success('Registrierung erfolgreich!', {
        description: 'Willkommen bei Cash-X!'
      });
      
      // Redirect to Smart Onboarding
      navigate('/smart-onboarding');
      
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Registrierung fehlgeschlagen', {
        description: error.message
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen py-12 px-4"
      style={{ backgroundColor: '#0B1220', color: '#E8ECF2' }}
    >
      <Toaster position="top-center" />
      
      <div className="max-w-4xl mx-auto">
        {/* Back to Landing */}
        <Link 
          to="/"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Zurück zur Startseite
        </Link>
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-10 h-10 bg-[#2F80FF] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">C</span>
            </div>
            <span 
              className="text-2xl font-bold text-white"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Cash-X
            </span>
          </Link>
          <h1 
            className="text-3xl font-bold mb-2"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Kostenlos testen
          </h1>
          <p className="text-slate-400">14 Tage kostenlos • Keine Kreditkarte erforderlich</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card 
            className="p-6 md:p-8"
            style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Plan Selection */}
              <div>
                <Label className="text-white mb-3 block">Wählen Sie Ihr Paket</Label>
                <RadioGroup
                  value={formData.plan}
                  onValueChange={(value) => setFormData({...formData, plan: value})}
                  className="grid grid-cols-1 md:grid-cols-3 gap-4"
                >
                  {plans.map((plan) => (
                    <div key={plan.id}>
                      <RadioGroupItem
                        value={plan.id}
                        id={plan.id}
                        className="peer sr-only"
                      />
                      <Label
                        htmlFor={plan.id}
                        data-testid={`plan-${plan.id}`}
                        className={`flex flex-col p-4 rounded-lg border cursor-pointer transition-all
                          peer-data-[state=checked]:border-[#2F80FF] peer-data-[state=checked]:bg-[#2F80FF]/10
                          ${plan.popular ? 'border-[#2F80FF]/50' : 'border-slate-700'}
                          hover:border-[#2F80FF]/70
                        `}
                        style={{ backgroundColor: formData.plan === plan.id ? 'rgba(47,128,255,0.1)' : '#0E1628' }}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-semibold text-white">{plan.name}</span>
                          {plan.popular && (
                            <span className="text-xs px-2 py-0.5 rounded bg-[#2F80FF]/20 text-[#60A5FA]">
                              Beliebt
                            </span>
                          )}
                        </div>
                        <div className="flex items-baseline gap-1 mb-2">
                          <span className="text-2xl font-bold text-white">{plan.price}€</span>
                          <span className="text-slate-400 text-sm">/Monat</span>
                        </div>
                        <p className="text-xs text-slate-400 mb-2">{plan.description}</p>
                        <ul className="text-xs text-slate-400 space-y-1">
                          {plan.features.map((f, i) => (
                            <li key={i} className="flex items-center gap-1">
                              <Check className="h-3 w-3 text-emerald-400" />
                              {f}
                            </li>
                          ))}
                        </ul>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {/* Contact & Company */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="company_name" className="text-white">Firmenname *</Label>
                  <Input
                    id="company_name"
                    data-testid="register-company-name"
                    placeholder="z.B. Pizzeria Milano"
                    value={formData.company_name}
                    onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                    className="mt-1 bg-[#0E1628] border-slate-700 text-white placeholder:text-slate-500"
                  />
                  {errors.company_name && (
                    <p className="text-red-400 text-xs mt-1">{errors.company_name}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="contact_name" className="text-white">Ansprechpartner *</Label>
                  <Input
                    id="contact_name"
                    data-testid="register-contact-name"
                    placeholder="Vor- und Nachname"
                    value={formData.contact_name}
                    onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                    className="mt-1 bg-[#0E1628] border-slate-700 text-white placeholder:text-slate-500"
                  />
                  {errors.contact_name && (
                    <p className="text-red-400 text-xs mt-1">{errors.contact_name}</p>
                  )}
                </div>
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="email" className="text-white">E-Mail-Adresse *</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="register-email"
                  placeholder="ihre@email.de"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="mt-1 bg-[#0E1628] border-slate-700 text-white placeholder:text-slate-500"
                />
                {errors.email && (
                  <p className="text-red-400 text-xs mt-1">{errors.email}</p>
                )}
              </div>

              {/* Passwords */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="password" className="text-white">Passwort *</Label>
                  <div className="relative mt-1">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      data-testid="register-password"
                      placeholder="Min. 8 Zeichen"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      className="bg-[#0E1628] border-slate-700 text-white placeholder:text-slate-500 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-red-400 text-xs mt-1">{errors.password}</p>
                  )}
                  <p className="text-slate-500 text-xs mt-1">
                    Min. 8 Zeichen, 1 Zahl, 1 Sonderzeichen
                  </p>
                </div>
                <div>
                  <Label htmlFor="password_confirm" className="text-white">Passwort bestätigen *</Label>
                  <Input
                    id="password_confirm"
                    type={showPassword ? 'text' : 'password'}
                    data-testid="register-password-confirm"
                    placeholder="Passwort wiederholen"
                    value={formData.password_confirm}
                    onChange={(e) => setFormData({...formData, password_confirm: e.target.value})}
                    className="mt-1 bg-[#0E1628] border-slate-700 text-white placeholder:text-slate-500"
                  />
                  {errors.password_confirm && (
                    <p className="text-red-400 text-xs mt-1">{errors.password_confirm}</p>
                  )}
                </div>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                data-testid="register-submit"
                className="w-full bg-[#2F80FF] hover:bg-[#4A92FF] text-white h-12 text-base font-medium"
                disabled={isLoading}
              >
                {isLoading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Wird erstellt...</>
                ) : (
                  'Kostenlos registrieren'
                )}
              </Button>

              {/* Legal */}
              <p className="text-xs text-slate-500 text-center">
                Mit der Registrierung akzeptieren Sie unsere{' '}
                <Link to="/agb" className="text-[#2F80FF] hover:underline">AGB</Link>{' '}
                und{' '}
                <Link to="/datenschutz" className="text-[#2F80FF] hover:underline">Datenschutzerklärung</Link>.
              </p>

              {/* Login Link */}
              <p className="text-center text-slate-400">
                Bereits registriert?{' '}
                <Link to="/login" className="text-[#2F80FF] hover:underline">Jetzt anmelden</Link>
              </p>
            </form>
          </Card>
        </motion.div>

        {/* Trust Badges */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="flex justify-center gap-6 mt-8 text-slate-400 text-sm"
        >
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-emerald-400" />
            TSE-zertifiziert
          </div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-blue-400" />
            DSGVO-konform
          </div>
        </motion.div>
      </div>
    </div>
  );
}
